/**
 * AP-7 — DBC-Browser (FM-WIN-01).
 *
 * Oeffnet immer als eigenes Fenster, nie eingebettet. Nicht live: der Inhalt
 * wird beim Oeffnen und nach dem Laden einer DBC aus dem `dbc`-Snapshot des
 * Workers geholt.
 */

import { createLabel } from '../ui/controls';
import { createFloatingPanel, type FloatingPanel } from './filters/FloatingPanel';
import { createDataTable } from './dataTable';
import { createSplitter } from '../ui/controls';
import { formatDbcId, formatG, type DbcMessageMeta, type DbcSnapshot } from './signalValue';
import './views.css';

const MESSAGE_COLUMNS = ['ID', 'Name', 'DLC', 'Signals'] as const;
const SIGNAL_COLUMNS = ['Signal', 'Start', 'Len', 'Order', 'Type', 'Scale', 'Offset', 'Unit'] as const;

export interface DbcBrowserOptions {
  /** Liefert `getSnapshot('dbc')` des Workers. */
  loadSnapshot: () => Promise<DbcSnapshot>;
}

export interface DbcBrowserHandle {
  panel: FloatingPanel;
  /** Beim Oeffnen und nach jedem DBC-Laden aufrufen. */
  refresh: () => Promise<void>;
  show: () => void;
}

export function createDbcBrowser(options: DbcBrowserOptions): DbcBrowserHandle {
  const panel = createFloatingPanel('DBC Browser');

  const view = document.createElement('div');
  view.className = 'cm-view';

  const searchRow = document.createElement('div');
  searchRow.className = 'cm-view__row';
  const search = document.createElement('input');
  search.type = 'text';
  search.className = 'cm-input';
  const summary = document.createElement('span');
  summary.className = 'cm-view__summary';
  summary.textContent = 'Messages: 0/0';
  searchRow.append(createLabel('Search:'), search, summary);

  const split = document.createElement('div');
  split.className = 'cm-view__split';

  let messages: DbcMessageMeta[] = [];
  /** Zeile der linken Liste -> Index in `messages` (wie `filteredIndices_`). */
  let filteredIndices: number[] = [];

  const signalTable = createDataTable({ columns: SIGNAL_COLUMNS, emptyText: '' });
  const messageTable = createDataTable({
    columns: MESSAGE_COLUMNS,
    emptyText: 'Keine DBC geladen.',
    onSelect: (row) => showSignals(row),
  });

  const splitter = createSplitter(split, {
    orientation: 'vertical',
    minRatio: 0.15, // FM-WIN-01: Grenzen 0.15-0.85
    onRatioChanged: (ratio) => split.style.setProperty('--split-ratio', String(ratio)),
  });
  split.append(messageTable.element, splitter, signalTable.element);

  view.append(searchRow, split);
  panel.body.append(view);

  function showSignals(row: number): void {
    const messageIndex = filteredIndices[row];
    const message = messageIndex === undefined ? undefined : messages[messageIndex];
    if (message === undefined) {
      signalTable.clear();
      return;
    }
    signalTable.setRows(
      message.signals.map((sig) => [
        sig.name,
        String(sig.startBit),
        String(sig.length),
        sig.bigEndian ? 'Motorola' : 'Intel',
        sig.isSigned ? 'Signed' : 'Unsigned',
        formatG(sig.scale),
        formatG(sig.offset),
        sig.unit,
      ]),
    );
  }

  /** PopulateMessages(): Suche nach Name, Gross-/Kleinschreibung egal. */
  function populate(): void {
    const query = search.value.toLowerCase();
    filteredIndices = [];
    messages.forEach((message, index) => {
      if (query !== '' && !message.name.toLowerCase().includes(query)) return;
      filteredIndices.push(index);
    });

    messageTable.setRows(
      filteredIndices.map((index) => {
        const message = messages[index] as DbcMessageMeta;
        return [
          formatDbcId(message.frameId, message.extended),
          message.name,
          String(message.dlc),
          String(message.signals.length),
        ];
      }),
    );
    summary.textContent = `Messages: ${filteredIndices.length}/${messages.length}`;
    signalTable.clear();
  }

  search.addEventListener('input', () => populate());

  async function refresh(): Promise<void> {
    try {
      const snapshot = await options.loadSnapshot();
      messages = snapshot.messages ?? [];
    } catch {
      messages = []; // ohne geladene DBC ist die leere Liste die richtige Antwort
    }
    populate();
  }

  return {
    panel,
    refresh,
    show() {
      panel.show();
      void refresh();
    },
  };
}
